import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UploadCloud, FileSpreadsheet, ArrowRight, CheckCircle2, AlertCircle, ArrowLeftRight, Sparkles, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatPriceINR, formatKm } from "../lib/format";

const TABS = [
  { key: "new",       label: "New",       Icon: Sparkles,        colour: "text-[#7A1519]" },
  { key: "updated",   label: "Updated",   Icon: ArrowLeftRight,  colour: "text-amber-700" },
  { key: "unchanged", label: "Unchanged", Icon: CheckCircle2,    colour: "text-[#242424]/70" },
  { key: "missing",   label: "Missing",   Icon: EyeOff,          colour: "text-[#242424]/70" },
];

const fmt = (field, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (field === "price") return formatPriceINR(v);
  if (field === "kilometres") return formatKm(v);
  return String(v);
};

const AdminImportCarTrade = () => {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [tab, setTab] = useState("new");
  const [missingAction, setMissingAction] = useState("leave_unchanged");
  const [confirming, setConfirming] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState(null);

  const chooseFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(null);
    setResult(null);
    setErrorMsg("");
  };

  const uploadPreview = async () => {
    if (!file) return;
    setUploading(true);
    setErrorMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/import/cartrade/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      const firstWith = TABS.find((t) => (r.data.counts?.[t.key] || 0) > 0)?.key || "new";
      setTab(firstWith);
      toast.success("Preview ready — nothing has been imported yet");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not parse spreadsheet";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const openConfirm = () => {
    if (!preview) return;
    const missingCount = preview.counts?.missing || 0;
    const total = (preview.counts?.new || 0) + (preview.counts?.updated || 0);
    if (!(total || (missingCount && missingAction !== "leave_unchanged"))) {
      toast.info("Nothing to apply. Choose a missing action or update the file.");
      return;
    }
    setErrorMsg("");
    setShowConfirmModal(true);
  };

  const doConfirmImport = async () => {
    if (!preview || confirming) return;
    setConfirming(true);
    setErrorMsg("");
    try {
      const r = await api.post(
        "/admin/import/cartrade/confirm",
        { preview_id: preview.preview_id, missing_action: missingAction },
        { timeout: 180000 }
      );
      setResult(r.data);
      setPreview(null);
      setFile(null);
      setShowConfirmModal(false);
      toast.success("Import applied");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Import failed";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setConfirming(false);
    }
  };

  const StatCard = ({ label, value, tone = "default" }) => (
    <div className={`border hairline p-5 rounded-sm ${tone === "primary" ? "bg-[#7A1519] text-white border-[#7A1519]" : "bg-white"}`}>
      <div className={`overline ${tone === "primary" ? "text-white/70" : "text-[#242424]/60"}`}>{label}</div>
      <div className={`mt-1 font-display text-3xl font-semibold ${tone === "primary" ? "text-white" : "text-[#111111]"}`}>{value}</div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="admin-import-cartrade">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => nav("/admin/inventory")} className="text-sm text-[#242424]/70 inline-flex items-center gap-2 mb-2"><ArrowLeft size={14} /> Back to Inventory</button>
          <div className="overline text-[#7A1519]">Bulk Import · CarTrade Exchange</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold">Import CarTrade Stock Report</h1>
          <p className="text-sm text-[#242424]/70 mt-2 max-w-2xl">Upload the CarTrade Stock Report Excel file. We'll parse it, compare against your existing inventory, and preview changes before anything is applied.</p>
        </div>
        <Link to="/admin/imports/history" className="border hairline bg-white px-4 py-2.5 rounded-sm text-sm hover:border-[#7A1519] transition-colors" data-testid="import-history-link">Import History →</Link>
      </div>

      {/* Result banner */}
      {result && (
        <div className="bg-white border-l-4 border-[#7A1519] border hairline p-6 rounded-sm" data-testid="import-result">
          <div className="flex items-center gap-2 overline text-[#7A1519]"><CheckCircle2 size={14} /> Import successful</div>
          <div className="mt-2 font-display text-2xl text-[#111111]">
            {result.counts.new} vehicle{result.counts.new === 1 ? "" : "s"} added
            {result.counts.updated > 0 && <span> · {result.counts.updated} updated</span>}
            {result.counts.missing_applied > 0 && <span> · {result.counts.missing_applied}/{result.counts.missing_seen} missing actioned</span>}
          </div>
          <div className="mt-1 text-sm text-[#242424]/80">
            {result.counts.unchanged} unchanged. You can now upload photos or refine individual vehicles from the inventory.
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to="/admin/inventory" className="bg-[#7A1519] text-white px-5 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center gap-2" data-testid="import-goto-inventory">
              Go to Inventory <ArrowRight size={14} />
            </Link>
            <Link to="/admin/imports/history" className="border hairline bg-white px-5 py-2.5 rounded-sm text-sm inline-flex items-center gap-2" data-testid="import-goto-history">
              View Import History
            </Link>
            <button onClick={() => { setResult(null); setFile(null); }} className="border hairline bg-white px-5 py-2.5 rounded-sm text-sm" data-testid="import-do-another">
              Run another import
            </button>
          </div>
        </div>
      )}

      {/* Inline error surface */}
      {errorMsg && !confirming && (
        <div className="bg-white border-l-4 border-[#7A1519] border hairline p-4 rounded-sm text-sm" data-testid="import-error">
          <div className="overline text-[#7A1519] mb-1">Something went wrong</div>
          <div className="text-[#242424]">{errorMsg}</div>
        </div>
      )}

      {/* Uploader */}
      {!preview && (
        <div className="bg-white border hairline p-8 rounded-sm">
          <div className="overline text-[#7A1519] mb-4">Step 1 · Upload file</div>
          <label className="border-2 border-dashed hairline block p-10 text-center cursor-pointer hover:border-[#7A1519] transition-colors rounded-sm">
            <FileSpreadsheet size={40} className="mx-auto text-[#7A1519]" />
            <div className="mt-3 text-base font-medium">{file ? file.name : "Select the CarTrade Stock Report"}</div>
            <div className="text-sm text-[#242424]/70 mt-1">.xls / .xlsx · Up to 10 MB</div>
            <input type="file" accept=".xls,.xlsx" hidden onChange={(e) => chooseFile(e.target.files?.[0])} data-testid="import-file-input" />
          </label>
          <div className="mt-5 flex justify-end">
            <button onClick={uploadPreview} disabled={!file || uploading} className="bg-[#7A1519] text-white px-6 py-3 rounded-sm text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid="import-preview-btn">
              {uploading ? "Parsing…" : <>Preview Changes <ArrowRight size={14} /></>}
            </button>
          </div>
          <div className="mt-8 border-t hairline pt-6 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <div className="overline text-[#242424]/60 mb-2">What will be synced</div>
              <div className="text-[#242424]/80">Price, kilometres, status, variant, colour, fuel, transmission, ownership, insurance, registration details.</div>
            </div>
            <div>
              <div className="overline text-[#242424]/60 mb-2">Protected from imports</div>
              <div className="text-[#242424]/80">Featured status, Autobahn photos, custom description, custom features, custom specs, SEO slug.</div>
            </div>
            <div>
              <div className="overline text-[#242424]/60 mb-2">Photos</div>
              <div className="text-[#242424]/80">Imported vehicles use the branded "Image coming soon" placeholder until you upload photos in the vehicle editor.</div>
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <>
          {(() => {
            const needsReviewCount = (preview.new || []).filter((v) => v.needs_review).length;
            if (!needsReviewCount) return null;
            return (
              <div className="bg-white border-l-4 border-[#7A1519] border hairline p-5 rounded-sm" data-testid="import-needs-review-banner">
                <div className="flex items-start gap-3">
                  <AlertCircle size={18} className="text-[#7A1519] mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <div className="font-semibold text-[#111111]">
                      {needsReviewCount} vehicle{needsReviewCount === 1 ? "" : "s"} need admin review before going live
                    </div>
                    <div className="text-[#242424]/80 mt-1">
                      These vehicles have a CarTrade status that Autobahn doesn't recognize. They'll be imported as <span className="font-semibold">Needs Review</span> and stay hidden from the public site until you open each one and assign the correct status.
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="New" value={preview.counts.new} tone={preview.counts.new ? "primary" : "default"} />
            <StatCard label="Updated" value={preview.counts.updated} />
            <StatCard label="Unchanged" value={preview.counts.unchanged} />
            <StatCard label="Missing from Report" value={preview.counts.missing} />
          </div>

          <div className="bg-white border hairline rounded-sm">
            <div className="flex border-b hairline overflow-x-auto">
              {TABS.map((t) => {
                const count = preview.counts[t.key] || 0;
                return (
                  <button key={t.key} onClick={() => setTab(t.key)} className={`px-5 py-3 text-sm inline-flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${tab === t.key ? "border-[#7A1519] text-[#7A1519]" : "border-transparent text-[#242424]/75"}`} data-testid={`import-tab-${t.key}`}>
                    <t.Icon size={14} /> {t.label} <span className="text-[#242424]/60">({count})</span>
                  </button>
                );
              })}
            </div>
            <div className="p-4 md:p-6">
              {tab === "new" && <NewList items={preview.new} />}
              {tab === "updated" && <UpdatedList items={preview.updated} />}
              {tab === "unchanged" && <SimpleList items={preview.unchanged} emptyText="No unchanged vehicles." />}
              {tab === "missing" && (
                <MissingList
                  items={preview.missing}
                  missingAction={missingAction}
                  setMissingAction={setMissingAction}
                />
              )}
            </div>
          </div>

          <div className="bg-[#111111] text-white p-6 rounded-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4 sticky bottom-4">
            <div>
              <div className="overline text-white/60">Ready to apply?</div>
              <div className="mt-1 font-display text-lg">
                {preview.counts.new} new · {preview.counts.updated} updated · {preview.counts.unchanged} unchanged · {preview.counts.missing} missing
              </div>
              <div className="text-xs text-white/60 mt-1">Missing action: <span className="text-white capitalize">{missingAction.replace(/_/g, " ")}</span></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setPreview(null); setFile(null); setErrorMsg(""); }} disabled={confirming} className="border border-white/20 text-white/80 px-5 py-3 rounded-sm text-sm disabled:opacity-50" data-testid="import-cancel">Discard</button>
              <button onClick={openConfirm} disabled={confirming} className="bg-[#7A1519] text-white px-6 py-3 rounded-sm text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid="import-confirm">
                Confirm Import <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* In-app confirm modal — avoids blocked window.confirm() in embedded environments */}
      {showConfirmModal && preview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-6" data-testid="import-confirm-modal">
          <div className="bg-white w-full max-w-md rounded-sm border hairline shadow-2xl p-8">
            {confirming ? (
              <div className="text-center py-4" data-testid="import-loading">
                <div className="mx-auto mb-4 h-10 w-10 border-2 border-[#7A1519] border-t-transparent rounded-full animate-spin" />
                <div className="font-display text-lg font-semibold text-[#111111]">
                  Importing {preview.counts.new} vehicle{preview.counts.new === 1 ? "" : "s"}…
                </div>
                <div className="text-sm text-[#242424]/75 mt-2">
                  This can take a few seconds. Please don't close this tab.
                </div>
              </div>
            ) : (
              <>
                <div className="overline text-[#7A1519]">Confirm Import</div>
                <div className="mt-3 font-display text-xl font-semibold text-[#111111]">
                  Apply these changes to Autobahn inventory?
                </div>
                <ul className="mt-4 text-sm text-[#242424] space-y-1.5 border hairline bg-[#F7F6F3] rounded-sm p-4">
                  <li><span className="font-semibold text-[#7A1519]">{preview.counts.new}</span> new vehicle{preview.counts.new === 1 ? "" : "s"} will be created</li>
                  <li><span className="font-semibold">{preview.counts.updated}</span> existing vehicle{preview.counts.updated === 1 ? "" : "s"} will be updated</li>
                  <li><span className="font-semibold">{preview.counts.unchanged}</span> unchanged</li>
                  <li><span className="font-semibold">{preview.counts.missing}</span> missing → <span className="capitalize">{missingAction.replace(/_/g, " ")}</span></li>
                </ul>
                {errorMsg && (
                  <div className="mt-3 text-sm text-[#7A1519] border-l-2 border-[#7A1519] pl-3">{errorMsg}</div>
                )}
                <div className="mt-6 flex gap-2 justify-end">
                  <button onClick={() => setShowConfirmModal(false)} className="border hairline px-5 py-2.5 rounded-sm text-sm" data-testid="import-modal-cancel">Cancel</button>
                  <button onClick={doConfirmImport} disabled={confirming} className="bg-[#7A1519] disabled:opacity-60 text-white px-5 py-2.5 rounded-sm text-sm font-semibold" data-testid="import-modal-confirm">
                    Yes, Import Now
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const NewList = ({ items }) => {
  if (!items?.length) return <Empty text="No new vehicles in this report." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F7F6F3] border-b hairline text-left text-[#242424]/60">
          <tr>
            <th className="px-3 py-2.5 font-medium">CarTrade ID</th>
            <th className="px-3 py-2.5 font-medium">Vehicle</th>
            <th className="px-3 py-2.5 font-medium">Price</th>
            <th className="px-3 py-2.5 font-medium">Km</th>
            <th className="px-3 py-2.5 font-medium">Fuel · Trans.</th>
            <th className="px-3 py-2.5 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.external_id} className={`border-b hairline ${it.needs_review ? "bg-[#F7F6F3]" : ""}`}>
              <td className="px-3 py-2.5 font-mono text-xs text-[#242424]/70">{it.external_id}</td>
              <td className="px-3 py-2.5">
                {it.summary || "—"}
                {it.needs_review && (
                  <span className="ml-2 text-[10px] tracking-[0.2em] uppercase font-semibold bg-[#7A1519] text-white px-2 py-0.5 rounded-sm align-middle" data-testid={`needs-review-${it.external_id}`}>
                    Needs Review
                  </span>
                )}
              </td>
              <td className="px-3 py-2.5 text-[#7A1519] font-semibold whitespace-nowrap">{formatPriceINR(it.price)}</td>
              <td className="px-3 py-2.5 whitespace-nowrap">{formatKm(it.kilometres)}</td>
              <td className="px-3 py-2.5">{it.fuel || "—"} · {it.transmission || "—"}</td>
              <td className="px-3 py-2.5">
                {it.needs_review ? (
                  <span title={`CarTrade status: "${it.raw_status || "empty"}"`} className="text-[#7A1519] font-semibold">
                    Needs Review
                  </span>
                ) : (
                  <span className="capitalize">{(it.status || "").replace("_", " ")}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const UpdatedList = ({ items }) => {
  if (!items?.length) return <Empty text="No fields changed for existing vehicles." />;
  return (
    <div className="space-y-3">
      {items.map((it) => (
        <div key={it.external_id} className="border hairline rounded-sm p-4" data-testid={`upd-${it.external_id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="font-medium">{it.summary}</div>
              <div className="text-xs text-[#242424]/60">CarTrade ID: <span className="font-mono">{it.external_id}</span></div>
            </div>
            <span className="text-[10px] tracking-[0.2em] uppercase font-semibold bg-amber-100 text-amber-800 px-2 py-1 rounded-sm">Will update</span>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {Object.entries(it.changes || {}).map(([field, ch]) => (
              <div key={field} className="border hairline bg-[#F7F6F3] rounded-sm px-3 py-2 flex items-center gap-2">
                <span className="text-xs uppercase tracking-[0.15em] text-[#242424]/60 font-semibold w-32 shrink-0">{field.replace(/_/g, " ")}</span>
                <span className="line-through text-[#242424]/60 text-xs">{fmt(field, ch.before)}</span>
                <ArrowRight size={12} className="text-[#7A1519] shrink-0" />
                <span className="text-[#111111] font-semibold">{fmt(field, ch.after)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const SimpleList = ({ items, emptyText }) => {
  if (!items?.length) return <Empty text={emptyText} />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F7F6F3] border-b hairline text-left text-[#242424]/60">
          <tr>
            <th className="px-3 py-2.5 font-medium">CarTrade ID</th>
            <th className="px-3 py-2.5 font-medium">Vehicle</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.external_id} className="border-b hairline">
              <td className="px-3 py-2.5 font-mono text-xs text-[#242424]/70">{it.external_id}</td>
              <td className="px-3 py-2.5">{it.summary}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MissingList = ({ items, missingAction, setMissingAction }) => (
  <div>
    <div className="border hairline bg-[#F7F6F3] rounded-sm p-4 mb-4">
      <div className="flex items-start gap-2">
        <AlertCircle size={16} className="text-[#7A1519] mt-0.5 shrink-0" />
        <div className="text-sm">
          These vehicles were imported from CarTrade before but are not in the current report.
          Choose what should happen to them. <span className="font-semibold">Nothing is deleted.</span>
        </div>
      </div>
      <div className="mt-4 flex flex-col md:flex-row gap-2">
        {[
          { key: "leave_unchanged", label: "Leave unchanged" },
          { key: "mark_sold",       label: "Mark as Sold" },
          { key: "mark_archived",   label: "Archive (hide from customers)" },
        ].map((opt) => (
          <label key={opt.key} className={`border hairline rounded-sm px-4 py-2.5 text-sm cursor-pointer flex-1 ${missingAction === opt.key ? "bg-[#7A1519] text-white border-[#7A1519]" : "bg-white"}`}>
            <input type="radio" hidden checked={missingAction === opt.key} onChange={() => setMissingAction(opt.key)} data-testid={`missing-${opt.key}`} />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
    {items?.length ? <SimpleList items={items} emptyText="" /> : <Empty text="Nothing missing — every previously imported vehicle is present." />}
  </div>
);

const Empty = ({ text }) => (
  <div className="text-center text-sm text-[#242424]/60 py-10">{text || "Nothing here."}</div>
);

export default AdminImportCarTrade;
